'use client';
import React, { useState, useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import Link from 'next/link';
import type { Language } from './AppLayout';

type AgentStatus = 'Available' | 'Busy' | 'Away' | 'After Call Work' | 'Offline';

const statusConfig: Record<AgentStatus, { dot: string; text: string; bg: string }> = {
  Available: { dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  Busy: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  Away: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  'After Call Work': { dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
  Offline: { dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-50 border-slate-200' },
};

interface TopStatusBarProps {
  onSearch?: () => void;
  language?: Language;
  onLanguageChange?: (lang: Language) => void;
  onMobileMenuToggle?: () => void;
}

export default function TopStatusBar({ onSearch, language = 'EN', onLanguageChange, onMobileMenuToggle }: TopStatusBarProps) {
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('Available');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notifCount, setNotifCount] = useState(3);
  const statusRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setShowStatusMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setShowNotifications(false);
      if (langRef.current && !langRef.current.contains(e.target as Node)) setShowLangMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (showSearch && searchRef.current) searchRef.current.focus();
  }, [showSearch]);

  const cfg = statusConfig[agentStatus];

  const notifications = [
    { id: 'n1', icon: 'ChatBubbleOvalLeftEllipsisIcon', color: 'text-green-600', title: 'New WhatsApp assigned', desc: 'Nurul Huda — Passport Renewal Documentation', time: '2m ago', unread: true, href: '/active-interaction-workspace' },
    { id: 'n2', icon: 'PhoneArrowDownLeftIcon', color: 'text-blue-600', title: 'Incoming call waiting', desc: 'Queue: MyGCC General · 1:24 wait', time: '5m ago', unread: true, href: '/active-interaction-workspace' },
    { id: 'n3', icon: 'ExclamationTriangleIcon', color: 'text-amber-600', title: 'Case approaching SLA', desc: 'MYGCC-2026-008415 · 45 min remaining', time: '8m ago', unread: true, href: '/cases' },
    { id: 'n4', icon: 'SparklesIcon', color: 'text-primary', title: 'AI draft ready for review', desc: 'Ahmed Firdaus · INT-2026-14892 · Voice', time: '12m ago', unread: false, href: '/active-interaction-workspace' },
    { id: 'n5', icon: 'BookOpenIcon', color: 'text-purple-600', title: 'Knowledge article updated', desc: 'Passport Renewal Guide — Jabatan Imigresen Malaysia', time: '1h ago', unread: false, href: '/knowledge-base' },
  ];

  const searchResults = searchQuery.length > 1 ? [
    { type: 'Citizen', label: 'Ahmed Firdaus', sub: '+60 12 *** 4581 · Selangor', icon: 'UserCircleIcon', href: '/active-interaction-workspace' },
    { type: 'Case', label: 'MYGCC-2026-008432', sub: 'MySejahtera Mobile Number Update · New', icon: 'ClipboardDocumentListIcon', href: '/cases' },
    { type: 'Interaction', label: 'INT-2026-14892', sub: 'Ahmed Firdaus · Voice · Active', icon: 'PhoneIcon', href: '/interactions' },
    { type: 'Knowledge', label: 'Updating Mobile Number in MySejahtera', sub: 'Kementerian Kesihatan Malaysia · Verified', icon: 'BookOpenIcon', href: '/knowledge-base' },
    { type: 'Case', label: 'MYGCC-2026-008415', sub: 'KWSP Withdrawal Eligibility · Awaiting Agency', icon: 'ClipboardDocumentListIcon', href: '/cases' },
  ].filter(r => r.label.toLowerCase().includes(searchQuery.toLowerCase()) || r.sub.toLowerCase().includes(searchQuery.toLowerCase())) : [];

  const t = (en: string, bm: string) => language === 'BM' ? bm : en;

  return (
    <header className="flex items-center justify-between bg-card border-b border-border px-4 h-13 flex-shrink-0 z-30" style={{ height: 52 }}>
      {/* Mobile hamburger + logo */}
      <div className="flex items-center gap-2 md:hidden">
        <button
          onClick={onMobileMenuToggle}
          className="p-2 rounded-lg hover:bg-muted transition-colors"
          aria-label="Open navigation menu"
        >
          <Icon name="Bars3Icon" size={20} className="text-foreground" />
        </button>
        <div className="flex items-center gap-1.5">
          <span className="text-[14px] font-700 text-foreground">MyGCC</span>
          <span className="text-[11px] text-primary font-600">CX CRM</span>
        </div>
      </div>

      {/* Desktop: Left: Agent Status */}
      <div className="hidden md:flex items-center gap-3">
        <div className="relative" ref={statusRef}>
          <button
            onClick={() => setShowStatusMenu(v => !v)}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[12px] font-600 transition-colors hover:shadow-sm ${cfg.bg}`}
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
            <span className={cfg.text}>{agentStatus}</span>
            <Icon name="ChevronDownIcon" size={11} className={cfg.text} />
          </button>
          {showStatusMenu && (
            <div className="absolute top-full left-0 mt-1.5 w-48 bg-card border border-border rounded-xl shadow-elevated z-50 py-1.5 slide-in-up">
              <div className="px-3 py-1 text-[10px] font-700 uppercase tracking-widest text-muted-foreground border-b border-border mb-1">
                {t('Agent Status', 'Status Ejen')}
              </div>
              {(Object.keys(statusConfig) as AgentStatus[]).map((s) => (
                <button
                  key={`status-${s}`}
                  onClick={() => { setAgentStatus(s); setShowStatusMenu(false); toast.success(`Status changed to ${s}`); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted transition-colors text-left"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig[s].dot}`} />
                  <span className="font-500 text-foreground">{s}</span>
                  {agentStatus === s && <Icon name="CheckIcon" size={12} className="ml-auto text-primary" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pl-3 border-l border-border">
          <Icon name="QueueListIcon" size={12} className="text-muted-foreground" />
          <span className="font-600 text-foreground">MyGCC General</span>
        </div>
      </div>

      {/* Centre: Stats — hidden on mobile */}
      <div className="hidden lg:flex items-center gap-5">
        {[
          { label: t('Interactions', 'Interaksi'), value: '32', icon: 'ChatBubbleLeftRightIcon', alert: false },
          { label: t('Avg. Handle', 'Purata Masa'), value: '04:18', icon: 'ClockIcon', alert: false },
          { label: t('Cases Pending', 'Kes Belum Selesai'), value: '7', icon: 'ClipboardDocumentListIcon', alert: true },
          { label: t('SLA Compliance', 'Pematuhan SLA'), value: '94.7%', icon: 'ShieldCheckIcon', alert: false },
        ].map((stat) => (
          <div key={`topstat-${stat.label}`} className="flex items-center gap-1.5">
            <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={13} className={stat.alert ? 'text-amber-500' : 'text-muted-foreground'} />
            <span className="text-[11px] text-muted-foreground">{stat.label}:</span>
            <span className={`text-[12px] font-700 font-tabular ${stat.alert ? 'text-amber-600' : 'text-foreground'}`}>{stat.value}</span>
          </div>
        ))}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1">
        {/* Search — hidden on mobile */}
        <div className="relative hidden sm:block">
          {showSearch ? (
            <div className="flex items-center gap-2 bg-muted rounded-lg px-2.5 py-1.5 border border-border">
              <Icon name="MagnifyingGlassIcon" size={13} className="text-muted-foreground flex-shrink-0" />
              <input
                ref={searchRef}
                type="text"
                placeholder={t('Search citizens, cases, knowledge...', 'Cari warganegara, kes, pengetahuan...')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onBlur={() => { if (!searchQuery) setShowSearch(false); }}
                className="bg-transparent text-[12px] outline-none w-52 text-foreground placeholder:text-muted-foreground"
              />
              <button onClick={() => { setShowSearch(false); setSearchQuery(''); }} className="text-muted-foreground hover:text-foreground">
                <Icon name="XMarkIcon" size={12} />
              </button>
              {searchResults.length > 0 && (
                <div className="absolute top-full right-0 mt-1.5 w-72 bg-card border border-border rounded-xl shadow-elevated z-50 py-1.5 slide-in-up">
                  {searchResults.map((r, i) => (
                    <button key={i} onClick={() => { toast.info(`Opening ${r.label}`); setShowSearch(false); setSearchQuery(''); }}
                      className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted transition-colors text-left">
                      <Icon name={r.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary flex-shrink-0" />
                      <div>
                        <div className="text-[12px] font-600 text-foreground">{r.label}</div>
                        <div className="text-[10px] text-muted-foreground">{r.sub}</div>
                      </div>
                      <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{r.type}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => setShowSearch(true)} className="p-2 rounded-lg hover:bg-muted transition-colors" title="Search (Ctrl+/)">
              <Icon name="MagnifyingGlassIcon" size={15} className="text-muted-foreground" />
            </button>
          )}
        </div>

        {/* Language Switcher */}
        <div className="relative" ref={langRef}>
          <button
            onClick={() => setShowLangMenu(v => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:bg-muted transition-colors text-[12px] font-600 text-foreground"
            title="Switch Language"
          >
            <Icon name="LanguageIcon" size={14} className="text-muted-foreground" />
            <span>{language}</span>
            <Icon name="ChevronDownIcon" size={11} className="text-muted-foreground hidden sm:inline" />
          </button>
          {showLangMenu && (
            <div className="absolute top-full right-0 mt-1.5 w-44 bg-card border border-border rounded-xl shadow-elevated z-50 py-1.5 slide-in-up">
              <div className="px-3 py-1 text-[10px] font-700 uppercase tracking-widest text-muted-foreground border-b border-border mb-1">
                Interface Language
              </div>
              {[
                { code: 'EN' as Language, label: 'English', sub: 'English' },
                { code: 'BM' as Language, label: 'Bahasa Malaysia', sub: 'Bahasa Malaysia' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    onLanguageChange?.(lang.code);
                    setShowLangMenu(false);
                    toast.success(`Language changed to ${lang.label}`);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted transition-colors text-left"
                >
                  <span className="font-500 text-foreground">{lang.label}</span>
                  {language === lang.code && <Icon name="CheckIcon" size={12} className="ml-auto text-primary" />}
                </button>
              ))}
              <div className="px-3 py-1.5 border-t border-border mt-1">
                <div className="text-[10px] text-muted-foreground">UI language only. Customer data preserved.</div>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setShowNotifications(v => !v); setNotifCount(0); }}
            className="relative p-2 rounded-lg hover:bg-muted transition-colors"
            title="Notifications"
          >
            <Icon name="BellIcon" size={15} className="text-muted-foreground" />
            {notifCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-700 rounded-full flex items-center justify-center notification-badge">
                {notifCount}
              </span>
            )}
          </button>
          {showNotifications && (
            <div className="absolute top-full right-0 mt-1.5 w-80 bg-card border border-border rounded-xl shadow-elevated z-50 slide-in-up">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h3 className="text-[13px] font-700">{t('Notifications', 'Pemberitahuan')}</h3>
                <button onClick={() => toast.info('All marked as read')} className="text-[11px] text-primary font-600 hover:underline">
                  {t('Mark all read', 'Tandai semua dibaca')}
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto scrollbar-thin">
                {notifications.map(n => (
                  <Link key={n.id} href={n.href || '/'} onClick={() => setShowNotifications(false)}
                    className={`w-full flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors text-left border-b border-border last:border-0 ${n.unread ? 'bg-blue-50/40' : ''}`}>
                    <div className={`p-1.5 rounded-lg bg-muted flex-shrink-0 mt-0.5`}>
                      <Icon name={n.icon as Parameters<typeof Icon>[0]['name']} size={13} className={n.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[12px] font-600 text-foreground">{n.title}</span>
                        {n.unread && <span className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0" />}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">{n.desc}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{n.time}</div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t border-border">
                <button onClick={() => setShowNotifications(false)} className="text-[12px] text-primary font-600 hover:underline">
                  {t('View all notifications →', 'Lihat semua pemberitahuan →')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Agent status dot */}
        <div className="relative md:hidden" ref={statusRef}>
          <button
            onClick={() => setShowStatusMenu(v => !v)}
            className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] font-600 transition-colors ${cfg.bg}`}
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
            <span className={`${cfg.text} hidden xs:inline`}>{agentStatus}</span>
          </button>
          {showStatusMenu && (
            <div className="absolute top-full right-0 mt-1.5 w-48 bg-card border border-border rounded-xl shadow-elevated z-50 py-1.5 slide-in-up">
              <div className="px-3 py-1 text-[10px] font-700 uppercase tracking-widest text-muted-foreground border-b border-border mb-1">
                {t('Agent Status', 'Status Ejen')}
              </div>
              {(Object.keys(statusConfig) as AgentStatus[]).map((s) => (
                <button
                  key={`mobile-status-${s}`}
                  onClick={() => { setAgentStatus(s); setShowStatusMenu(false); toast.success(`Status changed to ${s}`); }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[12px] hover:bg-muted transition-colors text-left"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusConfig[s].dot}`} />
                  <span className="font-500 text-foreground">{s}</span>
                  {agentStatus === s && <Icon name="CheckIcon" size={12} className="ml-auto text-primary" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* User — desktop only */}
        <div className="hidden md:flex items-center gap-2 ml-1 pl-2 border-l border-border">
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-700">NA</div>
          <div className="hidden sm:block">
            <div className="text-[11px] font-600 leading-tight">Nur Aisyah</div>
            <div className="text-[10px] text-muted-foreground leading-tight">CS Officer · MyGCC</div>
          </div>
        </div>
      </div>
    </header>
  );
}